export const EMULATOR_OPERATION = Object.freeze({
  NONE: 0x00,
  ADD: 0x01,
  SUBTRACT: 0x02,
  AND: 0x03,
  OR: 0x04,
  XOR: 0x05,
  SWAP: 0x06,
});

export const OPERATION_OPTIONS = Object.freeze([
  { value: "NONE", label: "Ninguna", needsOperand: false },
  { value: "ADD", label: "Suma", needsOperand: true },
  { value: "SUBTRACT", label: "Resta", needsOperand: true },
  { value: "AND", label: "AND", needsOperand: true },
  { value: "OR", label: "OR", needsOperand: true },
  { value: "XOR", label: "XOR", needsOperand: true },
  { value: "SWAP", label: "SWAP de bytes", needsOperand: false },
]);

// IDs de variable compartidos por LEER_VAR y el perfil del emulador.
export const EMULATOR_VARIABLE_ID = Object.freeze({
  VOLTAGE: 0x00,
  POWER: 0x01,
  IMPEDANCE: 0x02,
  AMBIENT_TEMPERATURE: 0x03,
  SURFACE_TEMPERATURE: 0x04,
  HUMIDITY: 0x05,
  PRESSURE: 0x06,
  ALTITUDE: 0x07,
  ACCELEROMETER: 0x08,
  GYROSCOPE: 0x09,
  MAGNETOMETER: 0x0a,
  GPS: 0x0b,
  LUV: 0x0c,
  STAR_TRACKER: 0x0d,
  RSSI: 0x0e,
  FREQUENCY: 0x0f,
  SNR: 0x10,
});

const direct = (value) => Number(value);
const offset = (amount) => (value) => Number(value) + amount;
const scale = (amount) => (value) => Number(value) * amount;
const negate = (value) => -Number(value);

export const EMULATOR_CATALOG = Object.freeze({
  EPS: [
    { key: "VOLTAGE", id: EMULATOR_VARIABLE_ID.VOLTAGE, name: "Voltaje", unit: "mV", min: 3000, max: 5000, step: 10, encode: direct, conversion: "Directo" },
    { key: "POWER", id: EMULATOR_VARIABLE_ID.POWER, name: "Potencia", unit: "unidad", min: 10, max: 100, step: 2, encode: direct, conversion: "Directo" },
    { key: "IMPEDANCE", id: EMULATOR_VARIABLE_ID.IMPEDANCE, name: "Impedancia", unit: "Ω", min: 100, max: 1000, step: 5, encode: direct, conversion: "Directo" },
  ],
  TLM: [
    { key: "AMBIENT_TEMPERATURE", id: EMULATOR_VARIABLE_ID.AMBIENT_TEMPERATURE, name: "Temperatura ambiente", unit: "°C", min: -10, max: 40, step: 2, encode: offset(40), conversion: "Enviado = real + 40" },
    { key: "SURFACE_TEMPERATURE", id: EMULATOR_VARIABLE_ID.SURFACE_TEMPERATURE, name: "Temperatura superficial", unit: "°C", min: -20, max: 60, step: 3, encode: offset(40), conversion: "Enviado = real + 40" },
    { key: "HUMIDITY", id: EMULATOR_VARIABLE_ID.HUMIDITY, name: "Humedad", unit: "%", min: 20, max: 80, step: 2, encode: direct, conversion: "Directo" },
    { key: "PRESSURE", id: EMULATOR_VARIABLE_ID.PRESSURE, name: "Presión", unit: "kPa", min: 90, max: 110, step: 1, encode: direct, conversion: "Directo" },
    { key: "ALTITUDE", id: EMULATOR_VARIABLE_ID.ALTITUDE, name: "Altitud", unit: "m", min: 0, max: 1000, step: 10, encode: direct, conversion: "Directo" },
    { key: "ACCELEROMETER", id: EMULATOR_VARIABLE_ID.ACCELEROMETER, name: "Acelerómetro", unit: "unidad", min: -200, max: 200, step: 1, encode: offset(200), conversion: "Enviado = real + 200" },
    { key: "GYROSCOPE", id: EMULATOR_VARIABLE_ID.GYROSCOPE, name: "Giroscopio", unit: "°/s", min: -250, max: 250, step: 5, encode: offset(250), conversion: "Enviado = real + 250" },
    { key: "MAGNETOMETER", id: EMULATOR_VARIABLE_ID.MAGNETOMETER, name: "Magnetómetro", unit: "unidad", min: -50, max: 50, step: 2, encode: offset(50), conversion: "Enviado = real + 50" },
    { key: "GPS", id: EMULATOR_VARIABLE_ID.GPS, name: "GPS · latitud", unit: "°", min: 19, max: 20, step: 0.01, encode: scale(100), conversion: "Enviado = grados × 100" },
    { key: "LUV", id: EMULATOR_VARIABLE_ID.LUV, name: "LUV", unit: "unidad", min: 0, max: 100, step: 3, encode: direct, conversion: "Directo" },
    { key: "STAR_TRACKER", id: EMULATOR_VARIABLE_ID.STAR_TRACKER, name: "Seguidor de estrellas", unit: "unidad", min: 0, max: 200, step: 1, encode: direct, conversion: "Directo" },
  ],
  COMMS: [
    { key: "RSSI", id: EMULATOR_VARIABLE_ID.RSSI, name: "RSSI", unit: "dBm", min: -80, max: -20, step: 2, encode: negate, conversion: "Mostrado = −enviado" },
    { key: "FREQUENCY", id: EMULATOR_VARIABLE_ID.FREQUENCY, name: "Frecuencia", unit: "MHz", min: 400, max: 450, step: 1, encode: direct, conversion: "Directo" },
    { key: "SNR", id: EMULATOR_VARIABLE_ID.SNR, name: "SNR", unit: "dB", min: 0, max: 30, step: 1, encode: direct, conversion: "Directo" },
  ],
});

export function encodeEmulatorValue(variable, value) {
  if (value === "" || value === null || value === undefined) return 0;
  const encoded = Math.round(variable.encode(value));
  if (!Number.isFinite(encoded) || encoded < 0 || encoded > 0xffff) {
    throw new Error(`${variable.name}: el valor codificado debe quedar entre 0 y 65535.`);
  }
  return encoded;
}

export function encodeThresholdPair(variable, low, high) {
  const encodedLow = encodeEmulatorValue(variable, low);
  const encodedHigh = encodeEmulatorValue(variable, high);
  return {
    low: Math.min(encodedLow, encodedHigh),
    high: Math.max(encodedLow, encodedHigh),
  };
}

export function hexByte(value) {
  return `0x${Number(value).toString(16).padStart(2, "0").toUpperCase()}`;
}
